import React, { useState } from 'react';
import { 
  Network, 
  Database, 
  Layers, 
  Cloud,
  Server,
  Lock,
  Settings,
  Plus,
  Edit,
  Trash,
  Save,
  X
} from 'lucide-react';

// Composants UI simplifiés
const Card = ({ children, className = '' }) => (
  <div className={`bg-white rounded-lg shadow-sm border border-gray-200 ${className}`}>
    {children}
  </div>
);

const CardHeader = ({ children, className = '' }) => (
  <div className={`px-6 py-4 border-b border-gray-200 ${className}`}>
    {children}
  </div>
);

const CardTitle = ({ children, className = '' }) => (
  <h3 className={`text-lg font-semibold text-gray-900 ${className}`}>
    {children}
  </h3>
);

const CardContent = ({ children, className = '' }) => (
  <div className={`px-6 py-4 ${className}`}>
    {children}
  </div>
);

const Button = ({ 
  children, 
  className = '', 
  variant = 'default', 
  size = 'default',
  onClick,
  ...props 
}) => {
  const baseStyles = 'inline-flex items-center justify-center rounded-md font-medium transition-colors';
  const variants = {
    default: 'bg-blue-500 text-white hover:bg-blue-600',
    outline: 'border border-gray-300 bg-white hover:bg-gray-100 text-gray-700',
    ghost: 'hover:bg-gray-100 text-gray-700'
  };
  const sizes = {
    default: 'h-10 px-4 py-2',
    sm: 'h-8 px-3 text-sm',
    lg: 'h-12 px-6'
  };

  return (
    <button
      className={`${baseStyles} ${variants[variant]} ${sizes[size]} ${className}`}
      onClick={onClick}
      {...props}
    >
      {children}
    </button>
  );
};


// Architecture data structure that can be externalized to API
const ARCHITECTURE_DATA = {
  overview: {
    title: "Architecture Système",
    description: "Architecture globale du système et ses composants principaux",
    lastUpdate: "2024-03-23",
    version: "1.0",
    status: "validated"
  },
  layers: [
    {
      id: "presentation",
      name: "Couche Présentation",
      icon: "Layers",
      components: [
        {
          id: "web-interface",
          name: "Interface Web",
          type: "frontend",
          technology: "React",
          description: "Interface utilisateur principale",
          dependencies: ["api-gateway"],
          security: ["HTTPS", "WAF"]
        },
        {
          id: "mobile-app",
          name: "Application Mobile",
          type: "frontend",
          technology: "React Native",
          description: "Application mobile cross-platform",
          dependencies: ["api-gateway"],
          security: ["SSL Pinning"]
        }
      ]
    },
    {
      id: "application",
      name: "Couche Application",
      icon: "Server",
      components: [
        {
          id: "api-gateway",
          name: "API Gateway",
          type: "service",
          technology: "Node.js",
          description: "Point d'entrée API centralisé",
          dependencies: ["auth-service", "core-services"],
          security: ["JWT", "Rate Limiting"]
        },
        {
          id: "auth-service",
          name: "Service d'Authentification",
          type: "service",
          technology: "Node.js",
          description: "Gestion de l'authentification",
          dependencies: ["user-db"],
          security: ["OAuth2", "2FA"]
        }
      ]
    },
    {
      id: "data",
      name: "Couche Données",
      icon: "Database",
      components: [
        {
          id: "main-db",
          name: "Base de Données Principale",
          type: "database",
          technology: "PostgreSQL",
          description: "Stockage principal des données",
          dependencies: [],
          security: ["Encryption", "Backup"]
        },
        {
          id: "cache",
          name: "Cache System",
          type: "cache",
          technology: "Redis",
          description: "Système de cache distribué",
          dependencies: ["main-db"],
          security: ["Encryption"]
        }
      ]
    }
  ],
  integrations: [
    {
      id: "payment-gateway",
      name: "Passerelle de Paiement",
      type: "external",
      provider: "Stripe",
      description: "Intégration système de paiement",
      security: ["PCI DSS"]
    },
    {
      id: "email-service",
      name: "Service Email",
      type: "external",
      provider: "SendGrid",
      description: "Service d'envoi d'emails",
      security: ["SPF", "DKIM"]
    }
  ]
};

// Component icons mapping
const ICONS_MAP = {
  Layers,
  Database,
  Network,
  Cloud,
  Server,
  Lock,
  Settings
};

// Helper component for editable fields
const EditableField = ({ value, onChange, isEditing, type = "text" }) => {
  if (!isEditing) return <span>{value}</span>;
  
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full p-2 border rounded"
    />
  );
};

// Component for layer visualization
const ArchitectureLayer = ({ layer, onEdit, onDelete, isEditing }) => {
  const IconComponent = ICONS_MAP[layer.icon] || Server;

  return (
    <Card className="mb-6">
      <CardHeader className="flex flex-row items-center justify-between">
        <div className="flex items-center space-x-4">
          <IconComponent className="h-6 w-6 text-blue-500" />
          <CardTitle>
            <EditableField
              value={layer.name}
              onChange={(value) => onEdit({ ...layer, name: value })}
              isEditing={isEditing}
            />
          </CardTitle>
        </div>
        {isEditing && (
          <div className="flex space-x-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDelete(layer.id)}
            >
              <Trash className="h-4 w-4" />
            </Button>
          </div>
        )}
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {layer.components.map((component) => (
            <ComponentCard
              key={component.id}
              component={component}
              isEditing={isEditing}
              onEdit={(updated) => {
                const updatedComponents = layer.components.map(c =>
                  c.id === updated.id ? updated : c
                );
                onEdit({ ...layer, components: updatedComponents });
              }}
              onDelete={(componentId) => {
                const filteredComponents = layer.components.filter(c =>
                  c.id !== componentId
                );
                onEdit({ ...layer, components: filteredComponents });
              }}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

// Component card visualization
const ComponentCard = ({ component, isEditing, onEdit, onDelete }) => {
  return (
    <div className="bg-white p-4 rounded-lg border shadow-sm">
      <div className="flex justify-between items-start">
        <div className="space-y-2">
          <h4 className="font-medium">
            <EditableField
              value={component.name}
              onChange={(value) => onEdit({ ...component, name: value })}
              isEditing={isEditing}
            />
          </h4>
          <p className="text-sm text-gray-600">
            <EditableField
              value={component.description}
              onChange={(value) => onEdit({ ...component, description: value })}
              isEditing={isEditing}
            />
          </p>
        </div>
        {isEditing && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onDelete(component.id)}
          >
            <Trash className="h-4 w-4" />
          </Button>
        )}
      </div>
      <div className="mt-4 space-y-2 text-sm">
        <div className="flex items-center space-x-2">
          <Settings className="h-4 w-4 text-gray-400" />
          <span>
            <EditableField
              value={component.technology}
              onChange={(value) => onEdit({ ...component, technology: value })}
              isEditing={isEditing}
            />
          </span>
        </div>
        <div className="flex items-center space-x-2">
          <Lock className="h-4 w-4 text-gray-400" />
          <span>{component.security.join(", ")}</span>
        </div>
      </div>
    </div>
  );
};

// Main architecture documentation component
const DocumentArchitecture = () => {
  const [architectureData, setArchitectureData] = useState(ARCHITECTURE_DATA);
  const [isEditing, setIsEditing] = useState(false);
  const [newLayerData, setNewLayerData] = useState(null);

  const handleLayerEdit = (updatedLayer) => {
    const updatedLayers = architectureData.layers.map(layer =>
      layer.id === updatedLayer.id ? updatedLayer : layer
    );
    setArchitectureData({ ...architectureData, layers: updatedLayers });
  };

  const handleLayerDelete = (layerId) => {
    const filteredLayers = architectureData.layers.filter(layer =>
      layer.id !== layerId
    );
    setArchitectureData({ ...architectureData, layers: filteredLayers });
  };

  const handleAddLayer = () => {
    setNewLayerData({
      id: `layer-${Date.now()}`,
      name: "Nouvelle Couche",
      icon: "Layers",
      components: []
    });
  };

  const saveNewLayer = () => {
    if (newLayerData) {
      setArchitectureData({
        ...architectureData,
        layers: [...architectureData.layers, newLayerData]
      });
      setNewLayerData(null);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {architectureData.overview.title}
          </h1>
          <p className="mt-1 text-gray-600">
            Version {architectureData.overview.version} - 
            Dernière mise à jour : {architectureData.overview.lastUpdate}
          </p>
        </div>
        <div className="flex space-x-4">
          <Button
            variant="outline"
            onClick={() => setIsEditing(!isEditing)}
            className="flex items-center space-x-2"
          >
            {isEditing ? (
              <>
                <Save className="h-4 w-4" />
                <span>Sauvegarder</span>
              </>
            ) : (
              <>
                <Edit className="h-4 w-4" />
                <span>Modifier</span>
              </>
            )}
          </Button>
          {isEditing && (
            <Button
              onClick={handleAddLayer}
              className="flex items-center space-x-2"
            >
              <Plus className="h-4 w-4" />
              <span>Ajouter une couche</span>
            </Button>
          )}
        </div>
      </div>

      {/* Architecture Layers */}
      <div className="space-y-6">
        {architectureData.layers.map((layer) => (
          <ArchitectureLayer
            key={layer.id}
            layer={layer}
            isEditing={isEditing}
            onEdit={handleLayerEdit}
            onDelete={handleLayerDelete}
          />
        ))}
      </div>

      {/* New Layer Modal */}
      {newLayerData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>Nouvelle Couche</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Nom
                  </label>
                  <input
                    type="text"
                    value={newLayerData.name}
                    onChange={(e) => setNewLayerData({
                      ...newLayerData,
                      name: e.target.value
                    })}
                    className="mt-1 w-full p-2 border rounded"
                  />
                </div>
                <div className="flex justify-end space-x-4">
                  <Button
                    variant="ghost"
                    onClick={() => setNewLayerData(null)}
                  >
                    Annuler
                  </Button>
                  <Button onClick={saveNewLayer}>
                    Ajouter
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default DocumentArchitecture;